/**
 * Thekedar's AI employees — the roles a business can "hire". Each one is a
 * role over the same audited action pipeline, not a separate bot.
 *
 * `available: true` means the role works today end to end. Roles that are
 * planned are shown as "joining soon" and can never be added or shown as
 * working. This file is the CATALOG of roles: the owner adds the roles they
 * need and names each employee themselves. `suggestedName` is only the
 * pre-filled suggestion; no code may treat it as a person's real name.
 */
export type EmployeeId =
  | "sales"
  | "follow_up"
  | "operations"
  | "accounts"
  | "marketing"
  | "reception"
  | "support";

export interface EmployeeRole {
  readonly id: EmployeeId;
  /** Pre-filled in the name field; the owner chooses the real name. */
  readonly suggestedName: string;
  readonly title: string;
  readonly available: boolean;
  /** One line for cards. */
  readonly pitch: string;
  /** What they actually do, in the owner's words. Only built behaviour for available roles. */
  readonly duties: readonly string[];
  /** Two-colour gradient for the avatar. */
  readonly hue: readonly [string, string];
}

export const employeeRoles: readonly EmployeeRole[] = [
  {
    id: "sales",
    suggestedName: "Priya",
    title: "Sales Executive",
    available: true,
    pitch:
      "Answers every enquiry in minutes, day or night, and moves it toward a sale.",
    duties: [
      "Replies on WhatsApp, Instagram and your enquiry link",
      "Speaks your customer's language — Hindi, Hinglish, Marathi and 6 more (AI writes each reply)",
      "Quotes only your prices; asks you when unsure",
      "Payment links from the chat, sent only with your OK",
    ],
    hue: ["#ffb547", "#ff6a3d"],
  },
  {
    id: "follow_up",
    suggestedName: "Arjun",
    title: "Follow-up Executive",
    available: true,
    pitch:
      "Never lets a quote go cold. Nudges quiet customers at the right time.",
    duties: [
      "Watches every open lead",
      "Drafts a polite nudge with AI when a follow-up is due",
      "Waits for your OK before anything goes out",
    ],
    hue: ["#2dd4bf", "#0e7490"],
  },
  {
    id: "operations",
    suggestedName: "Meera",
    title: "Office Manager",
    available: true,
    pitch:
      "Keeps the day organised and gives you a report with your morning chai.",
    duties: [
      "Morning summary email: new enquiries, sales, what needs you",
      "Opens it with a short friendly AI note; the numbers always come from your records, never from the AI",
      "Creates and tracks reply tasks",
      "Flags anything stuck or uncertain",
    ],
    hue: ["#a78bfa", "#6d28d9"],
  },
  {
    id: "reception",
    suggestedName: "Anjali",
    title: "Receptionist",
    available: true,
    pitch: "Books appointments and site visits, and sends reminders.",
    duties: [
      "A booking page customers use to pick a free slot (link and QR code)",
      "Never double-books; you set your hours, slot length and booking types",
      "Reminds customers the day before (fixed wording, no AI): sent with your OK, or handed to you to send from your WhatsApp",
      "Tells your sales employee your free slots so AI replies can offer them",
      "Asks you whether they came, so you can mark it done or no-show",
    ],
    hue: ["#f472b6", "#be185d"],
  },
  {
    id: "accounts",
    suggestedName: "Raghav",
    title: "Accountant",
    available: true,
    pitch: "Invoices, payment reminders and GST-ready records.",
    duties: [
      "Makes an invoice when a payment link is paid",
      "Numbers invoices in order and keeps GST-ready records for your CA",
      "Reminds customers politely when an invoice is overdue, worded by AI in their language (the invoice number, amount and link are never changed): sent with your OK, or handed to you to send from your WhatsApp",
      "Monthly sales register you can download",
    ],
    hue: ["#60a5fa", "#1d4ed8"],
  },
  {
    id: "marketing",
    suggestedName: "Zoya",
    title: "Marketing Manager",
    available: true,
    pitch: "Reads your Meta ads and Instagram, and tells you what's working.",
    duties: [
      "Shows which sources bring enquiries, replies and sales",
      "Reads your last 25 Instagram posts and, if connected, your Meta ads (read-only)",
      "Emails a weekly report every Monday morning, with a short AI note on what worked and what to try (numbers come from your records)",
      "Suggests 3 post ideas on request. Never posts or spends for you",
    ],
    hue: ["#facc15", "#ca8a04"],
  },
  {
    id: "support",
    suggestedName: "Kabir",
    title: "Customer Support",
    available: true,
    pitch: "Handles complaints, warranty and after-sales questions.",
    duties: [
      "Spots complaints, warranty, service and refund messages and opens a case for each",
      "Reads unclear messages with AI to judge whether it is a complaint and how urgent; urgent words always stay urgent",
      "Drafts a calm reply that never promises a refund or date for you",
      "Tracks every case against a 4-hour (urgent) or 24-hour target",
      "Checks in after a sale, and reminds you before a warranty ends",
    ],
    hue: ["#4ade80", "#15803d"],
  },
];

export const hireableRoles = employeeRoles.filter((role) => role.available);

/** One product, two kinds of workspace (`workspaces.kind`). */
export type WorkspaceKind = "business" | "creator";

/**
 * Creator titles for the same 7 roles. THE one place a creator's role titles live: every screen and prompt asks
 * `roleTitle(roleId, kind)` instead of reading `role.title`, so a creator never sees "Sales Executive".
 */
const creatorTitles: Readonly<Record<EmployeeId, string>> = {
  sales: "Brand deals",
  follow_up: "Brand follow-ups",
  operations: "Daily summary",
  accounts: "Invoices & payments",
  marketing: "Content",
  reception: "Calls & bookings",
  support: "Community (DMs)",
};

export function roleTitle(roleId: EmployeeId, kind: WorkspaceKind = "business"): string {
  const role = employeeRoles.find((candidate) => candidate.id === roleId);
  if (kind === "creator") return creatorTitles[roleId];
  return role?.title ?? roleId;
}

const creatorPitches: Readonly<Record<EmployeeId, string>> = {
  sales: "Drafts replies to brand messages and keeps your deals moving.",
  follow_up: "Nudges brands that have gone quiet. Waits for your OK.",
  operations: "A daily summary of what needs you.",
  accounts: "Invoices, payment links and reminders for late payments.",
  marketing: "Hooks, scripts, captions and cover pictures for your content.",
  reception: "A booking page for calls with brands.",
  support: "Spots messages that need a careful reply and keeps track of each one.",
};

/** The one-line pitch of a role, worded for the kind of workspace. */
export function rolePitch(roleId: EmployeeId, kind: WorkspaceKind = "business"): string {
  if (kind === "creator") return creatorPitches[roleId];
  return employeeRoles.find((candidate) => candidate.id === roleId)?.pitch ?? "";
}

/** Roles a creator workspace can hold: Reception (a booking page) and Support (shop-style cases) are for businesses. */
const creatorHidden: readonly EmployeeId[] = ["reception", "support"];

export function roleAllowed(roleId: EmployeeId, kind: WorkspaceKind = "business"): boolean {
  return kind === "business" || !creatorHidden.includes(roleId);
}

/** A new creator workspace starts with these three roles (exactly the Free limit of 3); a business starts with sales, follow-up, operations. */
export const creatorDefaultRoles: readonly EmployeeId[] = ["sales", "accounts", "marketing"];

/** Free plan: up to 3 employees on the team. Any paid plan: every role. */
export const EMPLOYEE_LIMIT_FREE = 3;

export const EMPLOYEE_NAME_MAX = 24;

/** Lower-case, trimmed, inner whitespace collapsed: the uniqueness key for a name. */
export function nameKey(name: string): string {
  return normalizeEmployeeName(name).toLowerCase();
}

/** NFC (composed and decomposed spellings are one name), smart apostrophes made plain, spaces collapsed, trimmed. */
export function normalizeEmployeeName(name: string): string {
  return name
    .normalize("NFC")
    .replace(/[\u2019\u02BC]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/** Words a name may not be, because they would hijack a role: every role id, title and the words in them ("sales", "office manager"). */
function roleWords(): Set<string> {
  const words = new Set<string>();
  for (const role of employeeRoles) {
    words.add(role.id.replace(/_/g, " "));
    words.add(role.id);
    words.add(role.title.toLowerCase());
    words.add(creatorTitles[role.id].toLowerCase());
  }
  for (const extra of [
    "accountant",
    "receptionist",
    "office manager",
    "manager",
    "executive",
    "follow-up",
    "customer support",
  ])
    words.add(extra);
  return words;
}
const reservedRoleWords = roleWords();

const reservedNames = new Set(["everyone", "team", "all"]);

/**
 * One validator for server and UI. Returns a message the owner can read, or
 * null when the name is fine. `takenKeys` = `nameKey`s of the OTHER active
 * employees (exclude the one being renamed).
 */
export function validateEmployeeName(
  name: string,
  takenKeys?: Iterable<string>,
): string | null {
  const clean = normalizeEmployeeName(name);
  if (!clean) return "Give them a name.";
  if ([...clean].length > EMPLOYEE_NAME_MAX)
    return `Keep the name to ${EMPLOYEE_NAME_MAX} characters or fewer.`;
  if (!/^[\p{L}\p{M} .'-]+$/u.test(clean))
    return "Use letters, spaces, dots, apostrophes or hyphens only.";
  if (!/\p{L}/u.test(clean)) return "The name needs at least one letter.";
  if (reservedRoleWords.has(clean.toLowerCase()))
    return `"${clean}" is a role, not a name. Pick a name for the person.`;
  if (reservedNames.has(clean.toLowerCase()))
    return `"${clean}" is kept for messages to the whole team. Pick another name.`;
  if (takenKeys) {
    const key = nameKey(clean);
    for (const taken of takenKeys)
      if (taken === key) return "Someone on your team already has that name.";
  }
  return null;
}

/** Roles to suggest first in onboarding, by business type. Always 3, none duplicated. */
const suggestedRoleMap: Record<string, readonly EmployeeId[]> = {
  salon: ["sales", "reception", "follow_up"],
  clinic: ["sales", "reception", "follow_up"],
  coaching: ["sales", "reception", "follow_up"],
  retail: ["sales", "follow_up", "marketing"],
  boutique: ["sales", "follow_up", "marketing"],
  food: ["sales", "follow_up", "marketing"],
  home_services: ["sales", "reception", "accounts"],
  interiors: ["sales", "reception", "accounts"],
  events: ["sales", "reception", "accounts"],
  travel: ["sales", "follow_up", "reception"],
  real_estate: ["sales", "follow_up", "reception"],
  other: ["sales", "follow_up", "operations"],
  creator: creatorDefaultRoles,
};

export function suggestedRolesFor(
  businessType: string | null | undefined,
): readonly EmployeeId[] {
  return suggestedRoleMap[businessType ?? "other"] ?? suggestedRoleMap.other!;
}

export function employeeRole(id: string): EmployeeRole | undefined {
  return employeeRoles.find((role) => role.id === id);
}
